import { useEffect, useState, useCallback, useRef } from "react";
import isUserPremium from "./isUserPremium";
import { trackPurchase } from "../analytics/metaPixel";

const POLL_INTERVALS = [2000, 3000, 4000, 5000, 6000, 8000, 10000];
const MAX_POLL_ATTEMPTS = POLL_INTERVALS.length;

function isReturningFromStripe() {
  const urlParams = new URLSearchParams(window.location.search);
  return (
    urlParams.has("claimPending") ||
    urlParams.has("checkout_success") ||
    document.referrer.includes("stripe.com") ||
    document.referrer.includes("checkout.stripe.com")
  );
}

const usePremiumStatus = (user, loading) => {
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [loadingStatus, setloadingStatus] = useState(!!user);
  const [justSubscribed, setJustSubscribed] = useState(false);
  const pollTimerRef = useRef(null);
  const pollAttemptRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollAttemptRef.current = 0;
  }, []);

  const checkPremiumStatus = useCallback(async () => {
    if (!user) {
      setPremiumStatus(null);
      setloadingStatus(false);
      return null;
    }
    setloadingStatus(true);
    const status = await isUserPremium();
    setPremiumStatus(status);
    setloadingStatus(false);
    return status;
  }, [user]);

  // Polls for premium status with increasing intervals until claims propagate
  const pollForPremiumStatus = useCallback(async () => {
    if (!user) return;

    const attempt = pollAttemptRef.current;
    if (attempt >= MAX_POLL_ATTEMPTS) {
      console.log("⏱️ Premium status polling finished — claims may still be propagating. User can refresh.");
      stopPolling();
      return;
    }

    console.log(`🔄 Polling premium status (attempt ${attempt + 1}/${MAX_POLL_ATTEMPTS})...`);
    const status = await isUserPremium();

    if (status && status !== false) {
      console.log("✅ Premium status confirmed:", status);
      setPremiumStatus(status);
      setloadingStatus(false);
      stopPolling();

      if (isReturningFromStripe()) {
        setJustSubscribed(true);
        // Subscriptions are reported server-side from the paid invoice — a trial
        // checkout collects $0 here, so reporting a Purchase now would teach Meta
        // to optimise for trial starts. One-off payments produce no invoice
        // webhook, so they remain the browser's responsibility.
        if (localStorage.getItem("pendingCheckoutMode") === "payment") {
          trackPurchase();
        }
      }

      // Clean up the checkout_success param from URL
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("checkout_success")) {
        urlParams.delete("checkout_success");
        const newQuery = urlParams.toString();
        const newUrl = window.location.pathname + (newQuery ? `?${newQuery}` : "");
        window.history.replaceState({}, "", newUrl);
      }
      return;
    }

    pollAttemptRef.current = attempt + 1;
    const nextDelay = POLL_INTERVALS[Math.min(attempt + 1, POLL_INTERVALS.length - 1)];
    pollTimerRef.current = setTimeout(pollForPremiumStatus, nextDelay);
  }, [user, stopPolling]);

  // Exposed refresh that can be called imperatively (e.g. after checkout)
  const refreshPremiumStatus = useCallback(async () => {
    stopPolling();
    const status = await checkPremiumStatus();
    if (!status || status === false) {
      // Claims haven't propagated yet — start polling
      pollAttemptRef.current = 0;
      pollTimerRef.current = setTimeout(pollForPremiumStatus, POLL_INTERVALS[0]);
    }
    return status;
  }, [checkPremiumStatus, pollForPremiumStatus, stopPolling]);

  // Initial check when user changes
  useEffect(() => {
    if (user) {
      checkPremiumStatus();
    } else {
      setPremiumStatus(null);
      setloadingStatus(false);
      stopPolling();
    }
  }, [user, checkPremiumStatus, stopPolling]);

  // Auto-poll when returning from Stripe checkout
  useEffect(() => {
    if (!user) return;
    if (!isReturningFromStripe()) return;

    console.log("🔄 Detected return from Stripe — starting premium status polling");

    // Give the webhook a moment to fire before the first check
    const initialDelay = setTimeout(() => {
      pollAttemptRef.current = 0;
      pollForPremiumStatus();
    }, 2000);

    return () => clearTimeout(initialDelay);
  }, [user, pollForPremiumStatus]);

  // Re-check when tab regains focus (handles users who switched away during checkout)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && user) {
        // Support repairs and purchases in another tab have no return URL.
        // Refresh those claims too, without starting checkout polling.
        if (isReturningFromStripe()) refreshPremiumStatus();
        else checkPremiumStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [user, refreshPremiumStatus, checkPremiumStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const clearJustSubscribed = useCallback(() => setJustSubscribed(false), []);

  return [premiumStatus, loadingStatus, refreshPremiumStatus, justSubscribed, clearJustSubscribed];
};

export default usePremiumStatus;
